import React from 'react'
import { LuGlobe } from 'react-icons/lu'

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox'

interface TimezoneGroup {
  value: string
  items: readonly string[]
}

function computeUtcOffset(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  }).formatToParts(new Date())
  const offsetText =
    parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  const match = offsetText.match(/[+-]\d{2}:\d{2}/)
  return match ? match[0] : '+00:00'
}

function buildTimezoneData(): {
  groups: readonly TimezoneGroup[]
  offsets: ReadonlyMap<string, string>
} {
  const allTimezones = Intl.supportedValuesOf('timeZone')
  const offsets = new Map<string, string>()
  const groupedByRegion = new Map<string, string[]>()

  for (const timezone of allTimezones) {
    offsets.set(timezone, computeUtcOffset(timezone))
    const region = timezone.includes('/') ? timezone.split('/')[0]! : 'Other'
    const bucket = groupedByRegion.get(region) ?? []
    bucket.push(timezone)
    groupedByRegion.set(region, bucket)
  }

  const groups = Array.from(groupedByRegion, ([value, items]) => ({
    value,
    items: items.sort((a, b) => a.localeCompare(b)),
  })).sort((a, b) => a.value.localeCompare(b.value))

  return { groups, offsets }
}

const { groups: timezoneGroups, offsets: timezoneOffsets } = buildTimezoneData()

function formatTimezone(timezone: string): string {
  return timezone.replaceAll('_', ' ').split('/').join(' - ')
}

interface Props {
  id?: string
  timezone: string
  onChange: (timezone: string) => void
  'aria-invalid'?: boolean
}

const TimezonePicker: React.FC<Props> = ({
  id,
  timezone,
  onChange,
  'aria-invalid': ariaInvalid,
}) => {
  return (
    <Combobox
      id={id}
      items={timezoneGroups}
      value={timezone}
      onValueChange={(newTimezone) => {
        if (newTimezone != null) {
          onChange(newTimezone)
        }
      }}
      itemToStringLabel={formatTimezone}
    >
      <ComboboxTrigger
        aria-invalid={ariaInvalid}
        className={`border-input flex h-8 w-fit items-center justify-between
          gap-2 rounded-md border bg-white px-3 whitespace-nowrap
          aria-invalid:border-destructive aria-invalid:ring-[3px]
          aria-invalid:ring-destructive/20`}
      >
        <LuGlobe />

        <ComboboxValue placeholder="Select timezone">
          {(selectedValue) =>
            typeof selectedValue === 'string' && selectedValue
              ? formatTimezone(selectedValue)
              : null
          }
        </ComboboxValue>
      </ComboboxTrigger>

      <ComboboxContent className="w-72">
        <ComboboxInput placeholder="Search timezones..." showTrigger={false} />

        <ComboboxList>
          <ComboboxEmpty>No matching timezones</ComboboxEmpty>

          <ComboboxCollection>
            {(group: TimezoneGroup) => (
              <ComboboxGroup items={group.items} key={group.value}>
                <ComboboxLabel>{group.value}</ComboboxLabel>

                <ComboboxCollection>
                  {(timezoneItem: string) => (
                    <ComboboxItem key={timezoneItem} value={timezoneItem}>
                      <span>
                        {formatTimezone(timezoneItem)}{' '}
                        <span className="text-muted-foreground">
                          ({timezoneOffsets.get(timezoneItem)})
                        </span>
                      </span>
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
              </ComboboxGroup>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export default TimezonePicker
